import React, { useState, useEffect } from 'react';
import { Users, MapPin, Clock, Mail, Heart, Zap, Shield } from 'lucide-react';

const VALUES = [
  {
    icon: Heart,
    title: 'Investor-First',
    description: 'Every feature we ship should help people make clearer, more confident investment decisions.',
  },
  {
    icon: Zap,
    title: 'Move with Purpose',
    description: 'We iterate quickly, measure what matters, and keep the product focused on real user needs.',
  },
  {
    icon: Shield,
    title: 'Trust by Design',
    description: 'Financial tools demand accuracy, security, and transparency — we hold ourselves to that standard.',
  },
];

const Careers = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/careers/roles');
        if (!res.ok) throw new Error('Failed to load open roles');
        const data = await res.json();
        if (!cancelled) {
          setRoles(Array.isArray(data.roles) ? data.roles : []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load open roles');
          setRoles([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="page-content info-page careers-page">
      <header className="info-page-header">
        <h2>
          <Users size={22} className="info-page-header-icon" aria-hidden />
          Careers at Bidsig
        </h2>
      </header>

      <div className="info-hero-card">
        <div className="info-hero-body">
          <p className="info-hero-lead">
            We&apos;re a small, focused team on a mission to give individual investors institutional-grade research tools — without the complexity. If you care about finance, data, and great product design, we&apos;d love to hear from you.
          </p>
        </div>
      </div>

      <section className="info-section" aria-labelledby="careers-values-title">
        <h2 id="careers-values-title" className="info-section-title">How We Work</h2>
        <div className="info-feature-grid info-feature-grid--3">
          {VALUES.map(({ icon: Icon, title, description }) => (
            <article key={title} className="info-card info-feature-card">
              <div className="info-card-icon info-card-icon--careers" aria-hidden>
                <Icon size={22} />
              </div>
              <h3 className="info-card-title">{title}</h3>
              <p className="info-card-text">{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="info-section" aria-labelledby="open-roles-title">
        <h2 id="open-roles-title" className="info-section-title">Open Roles</h2>

        {loading && (
          <p className="info-roles-status" role="status">Loading open roles…</p>
        )}

        {!loading && error && (
          <div className="info-roles-empty info-roles-empty--error" role="alert">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && roles.length === 0 && (
          <div className="info-roles-empty">
            <p>There are no current open roles at this time.</p>
            <p className="info-roles-empty-sub">
              Check back later, or send your résumé to{' '}
              <a href="mailto:careers@bidsig.com">careers@bidsig.com</a>{' '}
              if you&apos;d like us to keep you in mind for future opportunities.
            </p>
          </div>
        )}

        {!loading && !error && roles.length > 0 && (
          <div className="info-roles-list">
            {roles.map((role) => (
              <article key={role.id} className="info-card info-role-card">
                <div className="info-role-header">
                  <h3 className="info-card-title">{role.title}</h3>
                  <div className="info-role-meta">
                    <span className="info-role-tag">
                      <MapPin size={14} aria-hidden />
                      {role.location}
                    </span>
                    <span className="info-role-tag">
                      <Clock size={14} aria-hidden />
                      {role.type}
                    </span>
                  </div>
                </div>
                <p className="info-card-text">{role.summary}</p>
                <a href="mailto:careers@bidsig.com" className="info-role-apply">
                  <Mail size={16} aria-hidden />
                  Apply — careers@bidsig.com
                </a>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Careers;
